/* eslint-disable react-hooks/immutability -- shader uniforms need imperative mutation per frame */
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useTheme } from '@/hooks/useTheme';
import { onRipple, type RippleDropDetail } from '@/lib/ripple-bus';

const SIM_RES = 512;
const MAX_DROPS_PER_FRAME = 4;

const computeVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const computeFragment = /* glsl */ `
  uniform sampler2D uPrevState;
  uniform sampler2D uCurrState;
  uniform vec2 uTexelSize;
  uniform float uDamping;
  uniform float uPropagation;
  uniform vec2 uDropPos[${MAX_DROPS_PER_FRAME}];
  uniform float uDropStrength[${MAX_DROPS_PER_FRAME}];
  uniform float uDropRadius;
  uniform int uDropCount;
  varying vec2 vUv;

  void main() {
    float curr = texture2D(uCurrState, vUv).r;
    float prev = texture2D(uPrevState, vUv).r;

    float l = texture2D(uCurrState, vUv - vec2(uTexelSize.x, 0.0)).r;
    float r = texture2D(uCurrState, vUv + vec2(uTexelSize.x, 0.0)).r;
    float u = texture2D(uCurrState, vUv - vec2(0.0, uTexelSize.y)).r;
    float d = texture2D(uCurrState, vUv + vec2(0.0, uTexelSize.y)).r;

    float laplacian = l + r + u + d - 4.0 * curr;
    float next = 2.0 * curr - prev + uPropagation * laplacian;
    next *= uDamping;

    for (int i = 0; i < ${MAX_DROPS_PER_FRAME}; i++) {
      if (i >= uDropCount) break;
      float dist = distance(vUv, uDropPos[i]);
      next += exp(-dist * dist / (uDropRadius * uDropRadius)) * uDropStrength[i];
    }

    next = clamp(next, -1.0, 1.0);
    gl_FragColor = vec4(next, 0.0, 0.0, 1.0);
  }
`;

const renderVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const renderFragment = /* glsl */ `
  uniform sampler2D uState;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec2 vUv;

  void main() {
    float h = texture2D(uState, vUv).r;
    float peak = pow(max(h, 0.0), 0.35);
    gl_FragColor = vec4(uColor, clamp(peak * uOpacity, 0.0, uOpacity));
  }
`;

interface SimulationProps {
  damping: number;
  propagation: number;
  dropRadius: number;
  color: THREE.Color;
  opacity: number;
}

function Simulation({ damping, propagation, dropRadius, color, opacity }: SimulationProps) {
  const { gl } = useThree();
  const dropQueue = useRef<RippleDropDetail[]>([]);
  const flipRef = useRef(0);

  const fbos = useMemo(() => {
    const opts: THREE.RenderTargetOptions = {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
    };
    return [
      new THREE.WebGLRenderTarget(SIM_RES, SIM_RES, opts),
      new THREE.WebGLRenderTarget(SIM_RES, SIM_RES, opts),
      new THREE.WebGLRenderTarget(SIM_RES, SIM_RES, opts),
    ];
  }, []);

  const computeMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uPrevState: { value: null },
          uCurrState: { value: null },
          uTexelSize: { value: new THREE.Vector2(1 / SIM_RES, 1 / SIM_RES) },
          uDamping: { value: damping },
          uPropagation: { value: propagation },
          uDropPos: {
            value: Array.from({ length: MAX_DROPS_PER_FRAME }, () => new THREE.Vector2(-1, -1)),
          },
          uDropStrength: { value: new Array(MAX_DROPS_PER_FRAME).fill(0) },
          uDropRadius: { value: dropRadius },
          uDropCount: { value: 0 },
        },
        vertexShader: computeVertex,
        fragmentShader: computeFragment,
      }),
    [damping, propagation, dropRadius],
  );

  const renderMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uState: { value: null },
          uColor: { value: color.clone() },
          uOpacity: { value: opacity },
        },
        vertexShader: renderVertex,
        fragmentShader: renderFragment,
        transparent: true,
        depthWrite: false,
        depthTest: false,
      }),
    [color, opacity],
  );

  const computeScene = useMemo(() => {
    const scene = new THREE.Scene();
    const geom = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geom, computeMaterial);
    scene.add(mesh);
    return scene;
  }, [computeMaterial]);

  const orthoCamera = useMemo(() => new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), []);

  useEffect(() => {
    return onRipple((detail) => {
      dropQueue.current.push(detail);
    });
  }, []);

  useFrame(() => {
    const flip = flipRef.current;
    const prev = fbos[flip % 3]!;
    const curr = fbos[(flip + 1) % 3]!;
    const next = fbos[(flip + 2) % 3]!;

    const drops = dropQueue.current.splice(0, MAX_DROPS_PER_FRAME);
    const uniforms = computeMaterial.uniforms;
    const positions = uniforms.uDropPos!.value as THREE.Vector2[];
    const strengths = uniforms.uDropStrength!.value as number[];

    for (let i = 0; i < MAX_DROPS_PER_FRAME; i++) {
      if (i < drops.length) {
        const drop = drops[i]!;
        positions[i]!.set(drop.x, drop.y);
        strengths[i] = drop.strength ?? 0.6;
      } else {
        positions[i]!.set(-1, -1);
        strengths[i] = 0;
      }
    }
    uniforms.uDropCount!.value = drops.length;
    uniforms.uPrevState!.value = prev.texture;
    uniforms.uCurrState!.value = curr.texture;

    gl.setRenderTarget(next);
    gl.clear();
    gl.render(computeScene, orthoCamera);
    gl.setRenderTarget(null);

    renderMaterial.uniforms.uState!.value = next.texture;
    flipRef.current = flip + 1;
  });

  // Mesh fullscreen: usa o material de render que sample a textura de estado.
  // O R3F renderiza isso automaticamente com sua câmera ortho via prop do Canvas.
  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      <primitive object={renderMaterial} attach="material" />
    </mesh>
  );
}

export interface RippleWavesProps {
  /** Override de cor (hex). Se omitido, usa cores reativas ao tema. */
  color?: string;
  /** Opacidade máxima do render (0–1). */
  opacity?: number;
  /** Amortecimento por frame (próximo de 1 = ondas duram mais). */
  damping?: number;
  /** Velocidade de propagação (0.1–0.5 — quanto maior, mais rápido). */
  propagation?: number;
  /** Raio do drop em coords UV (0–1). */
  dropRadius?: number;
  /** zIndex do canvas. */
  zIndex?: number;
}

const THEME_COLORS = {
  light: '#475569', // slate-600 — dark sutil sobre fundo branco
  dark: '#a5b4fc', // indigo-300 — indigo claro sobre fundo preto
};

export function RippleWaves({
  color,
  opacity = 0.35,
  damping = 0.982,
  propagation = 0.245,
  dropRadius = 0.012,
  zIndex = 0,
}: RippleWavesProps) {
  const { theme } = useTheme();
  const resolvedColor = color ?? THEME_COLORS[theme];
  const threeColor = useMemo(() => new THREE.Color(resolvedColor), [resolvedColor]);

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={{ zIndex }}>
      <Canvas
        orthographic
        camera={{ position: [0, 0, 1], near: 0, far: 2, left: -1, right: 1, top: 1, bottom: -1 }}
        gl={{ antialias: false, alpha: true, premultipliedAlpha: false }}
        dpr={[1, 1.5]}
        frameloop="always"
        style={{ width: '100%', height: '100%' }}
      >
        <Simulation
          damping={damping}
          propagation={propagation}
          dropRadius={dropRadius}
          color={threeColor}
          opacity={opacity}
        />
      </Canvas>
    </div>
  );
}
